"use client";

import Link from 'next/link';
import Image from 'next/image';
import { siteConfig } from '@/data/siteConfig';
import { useEffect, useState } from 'react';
import { resolveApiUrl } from '@/data/runtime-data';

const navigationLinks = [
  { href: '/', label: 'Home' },
  { href: '/menu', label: 'Menu' },
  { href: '/about', label: 'Our Story' },
  { href: '/contact', label: 'Visit Us' },
];

export function Footer() {
  const [phoneDisplay, setPhoneDisplay] = useState(siteConfig.contact.phoneDisplay);
  const [phoneHref, setPhoneHref] = useState(siteConfig.contact.phoneHref);
  const [email, setEmail] = useState(siteConfig.contact.email);
  const [addressLine1, setAddressLine1] = useState(siteConfig.address.line1);
  const [addressLine2, setAddressLine2] = useState(siteConfig.address.line2);

  useEffect(() => {
    const loadContact = async () => {
      try {
        const res = await fetch(resolveApiUrl('/api/contact'), { cache: 'no-store' });
        if (!res.ok) return;
        const json = (await res.json()) as {
          phoneDisplay?: string;
          phoneHref?: string;
          email?: string;
          addressLine1?: string;
          addressLine2?: string;
        };
        if (json.phoneDisplay) setPhoneDisplay(json.phoneDisplay);
        if (json.phoneHref) setPhoneHref(json.phoneHref);
        if (json.email) setEmail(json.email);
        if (json.addressLine1) setAddressLine1(json.addressLine1);
        if (json.addressLine2) setAddressLine2(json.addressLine2);
      } catch (error) {
        console.warn('Failed to load live contact info for footer.', error);
      }
    };
    void loadContact();
  }, []);

  return (
    <footer className="bg-white border-t-2 border-gray-100">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <Link href="/" className="flex items-center space-x-3 mb-6 group">
              <div className="w-10 h-10 relative">
                <Image
                  src="/unchai-logo.png"
                  alt="Unchai Thai Restaurant logo"
                  fill
                  sizes="40px"
                  className="object-contain"
                  priority
                />
              </div>
              <span className="font-semibold text-lg text-gray-900">Unchai Thai Restaurant</span>
            </Link>
            <p className="text-gray-600 text-base leading-relaxed">
              Comforting Thai noodle soups, curries, and stir-fries in Vancouver. Visit us on Burrard Street
              for dine-in or call ahead for takeout.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Company</h3>
            <ul className="space-y-3">
              {navigationLinks.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="text-gray-600 hover:text-black transition-colors duration-200"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Menu Highlights</h3>
            <ul className="space-y-3 text-base">
              <li className="text-gray-600">Pad Thai with black tiger prawns</li>
              <li className="text-gray-600">Boat noodle soup with pork</li>
              <li className="text-gray-600">Green curry with prawns</li>
              <li className="text-gray-600">Pad kee mao stir-fry</li>
              <li className="text-gray-600">Sticky rice rainbow dessert</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Contact</h3>
            <ul className="space-y-3 text-base">
              <li className="text-gray-600">
                {addressLine1}
                <br />
                {addressLine2}
                <br />
                <Link
                  href={`mailto:${email}`}
                  className="text-gray-800 font-semibold hover:text-black"
                >
                  {email}
                </Link>
              </li>
              <li>
                <Link
                  href={phoneHref}
                  className="text-gray-900 hover:text-black font-medium transition-colors duration-200 block"
                >
                  {phoneDisplay}
                </Link>
                <Link
                  href="/contact"
                  className="text-gray-900 hover:text-black font-medium transition-colors duration-200 block mt-1"
                >
                  Visit &amp; hours →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t-2 border-gray-100 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="flex flex-wrap gap-6 mb-4 md:mb-0">
            <Link
              href="/privacy"
              className="text-gray-600 hover:text-black transition-colors duration-200 text-sm"
            >
              Privacy Policy
            </Link>
            <Link
              href="/security"
              className="text-gray-600 hover:text-black transition-colors duration-200 text-sm"
            >
              Security
            </Link>
          </div>
          <p className="text-gray-600 text-sm">
            © {new Date().getFullYear()} Unchai Thai Restaurant. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
